import { useEffect, useRef, useState } from 'react';
import { api, type FacilitySearchRow } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { SEARCH_DEBOUNCE_MS, SEARCH_LIMIT } from '../../ui/FacilityPicker';
import { sx } from '../../ui/designStyles';
import { useBuildLabels } from '../../ui/buildLabels';
import { pre } from './lib';

/**
 * A search row as the popover draws it. `where` (city, state) and the answer's `total`
 * are PROPOSED on GET /facilities/search: until the back end sends them the line under a
 * name is empty and the count is the rows found.
 */
type Row = FacilitySearchRow & { where?: string | null };
type Answer = { rows: Row[]; total?: number; real?: boolean };

/**
 * The facility popover (design capPickOpen T10010-10018, fnPickOpen T10209-10221): a
 * search field, a count line, and the facilities the server finds (the first page; the
 * full list is never loaded). Capture's list starts with "All facilities" (the network
 * view); Finance's lists buildings only and says when nothing matches. Escape or a click
 * outside closes it.
 */
export function FacilityPopover({
  current,
  onPick,
  onClose,
  top = 40,
  variant = 'capture',
  total,
}: {
  current: { id: string; name: string } | null;
  onPick: (f: { id: string; name: string } | null) => void;
  onClose: () => void;
  top?: number;
  variant?: 'capture' | 'finance';
  /** Facilities in the directory, when the page knows it (the search may not say). */
  total?: number | null;
}) {
  const { client } = useSession();
  const buildLabels = useBuildLabels();
  const ref = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState('');
  const [ans, setAns] = useState<Answer | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && !(e.target as HTMLElement).closest?.('[aria-haspopup="dialog"]')) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    const ctrl = new AbortController();
    const t = window.setTimeout(
      () => {
        api
          .facilitySearch(client, { query: { q: q.trim(), limit: SEARCH_LIMIT }, signal: ctrl.signal })
          .then((r) => {
            if (!ctrl.signal.aborted) setAns(r as unknown as Answer);
          })
          .catch(() => {
            if (!ctrl.signal.aborted) setAns({ rows: [] });
          });
      },
      q ? SEARCH_DEBOUNCE_MS : 0,
    );
    return () => {
      ctrl.abort();
      window.clearTimeout(t);
    };
  }, [q, client]);

  const term = q.trim().toLowerCase();
  const rows = ans?.rows ?? [];
  const n = ans?.total ?? total ?? (ans && !term ? rows.length : null);
  const sample = ans?.real === false && buildLabels ? ' · SAMPLE directory' : '';
  const capture = variant === 'capture';
  // Capture's "All facilities" row is found by the same words as the design (L23318).
  const allRow = capture && (!term || 'all every facility · roll-up'.includes(term));
  const hits = rows.length + (allRow ? 1 : 0);
  const meta = n == null ? '' : capture ? (term ? hits + ' match · ' : '') + n + ' facilities' + sample : (term ? rows.length + ' of ' : '') + n + ' facilities' + sample;
  const opt = (on: boolean) => 'display:flex;flex-direction:column;gap:1px;padding:7px 8px;border-radius:7px;text-decoration:none;' + (on ? 'background:rgba(47,92,151,.10);' : '');

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Choose a facility"
      style={sx('position:absolute;right:0;top:' + top + 'px;z-index:40;width:300px;background:#FCFAF4;border:1px solid rgba(34,39,46,.16);border-radius:10px;box-shadow:0 12px 32px rgba(34,39,46,.16);padding:8px;display:flex;flex-direction:column;gap:4px;' + (capture ? '' : 'white-space:normal;'))}
    >
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search facilities by name, city or state"
        aria-label="Search facilities"
        autoFocus
        style={sx('height:36px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:8px;font-family:Archivo,sans-serif;font-size:16px;background:#fff;color:#22272E;outline:none;min-width:0;')}
      />
      <span style={sx('font-size:14px;color:#54606C;padding:2px;')}>
        <span>{meta}</span>
      </span>
      <div role="listbox" aria-label="Facilities" style={sx('max-height:' + (capture ? 280 : 260) + 'px;overflow-y:auto;display:flex;flex-direction:column;')}>
        {allRow && (
          <a
            href="#"
            role="option"
            aria-selected={current === null}
            onClick={(e) => {
              pre(e);
              onPick(null);
            }}
            style={sx(opt(current === null))}
          >
            <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>
              <span>All facilities</span>
            </span>
            <span style={sx('font-size:14px;color:#54606C;')}>
              <span>every facility · roll-up</span>
            </span>
          </a>
        )}
        {rows.map((r) => (
          <a
            key={r.id}
            href="#"
            role="option"
            aria-selected={current?.id === r.id}
            onClick={(e) => {
              pre(e);
              onPick({ id: r.id, name: r.name });
            }}
            style={sx(opt(current?.id === r.id))}
          >
            <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>
              <span>{r.name}</span>
            </span>
            <span style={sx('font-size:14px;color:#54606C;')}>
              <span>{r.where ?? ''}</span>
            </span>
          </a>
        ))}
        {!capture && ans && rows.length === 0 && <span style={sx('font-size:16px;color:#54606C;padding:8px 4px;')}>No facility matches.</span>}
      </div>
    </div>
  );
}
